import React, { useEffect } from "react";
import {
  Scalars,
  useEditInvoiceMutation,
  useGetInvoiceDetailsQuery,
  useRecalculateInvoiceChargesMutation,
} from "../../../graphql/generated";
import InvoiceDetails, { InvoiceDetailsProps } from "./InvoiceDetails";
import LoadingOverlay from "../../common/LoadingOverlay";
import ErrorMessage from "../../common/ErrorMessage/ErrorMessage";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

type InvoiceDetailsContainerProps = Omit<
  InvoiceDetailsProps,
  "invoice" | "onSave" | "onRecalculate" | "onSeparated"
> & {
  invoiceId: Scalars["ObjectId"];
  onLoad?: (invoice: InvoiceDetailsProps["invoice"]) => void;
};

const InvoiceDetailsContainer: React.FC<InvoiceDetailsContainerProps> = ({
  invoiceId,
  onLoad,
  ...restProps
}) => {
  const { data, isLoading, error, refetch } = useGetInvoiceDetailsQuery({
    id: invoiceId,
  });

  const editInvoiceMutation = useEditInvoiceMutation();
  const recalculateInvoiceMutation = useRecalculateInvoiceChargesMutation();

  const { t } = useTranslation(["finance", "common"]);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (data?.invoiceById && onLoad) {
      onLoad(data.invoiceById);
    }
  }, [data, onLoad]);

  if (isLoading) {
    return <LoadingOverlay loading />;
  }
  if (error) {
    return <ErrorMessage error={error} />;
  }

  const handleSave = async (updatedInvoice: InvoiceDetailsProps["invoice"]) => {
    try {
      await editInvoiceMutation.mutateAsync({
        id: invoiceId,
        editInvoiceData: {
          charges: updatedInvoice.charges.map(c => ({
            ...c,
            billingRule: c.billingRule?._id,
            ratingTable: c.ratingTable?._id,
            customFields: c.customFields?.map((cf) => ({
              key: cf.key,
              value: cf.value,
            })),
          })),
          payments: updatedInvoice.payments,
          status: updatedInvoice.status,
          customFields: updatedInvoice.customFields?.map((cf) => ({
            key: cf.key,
            value: cf.value,
          })),
        },
      });
      enqueueSnackbar(
        t("invoiceDetails.invoiceSaveSuccess", "Invoice saved successfully"),
        {
          variant: "success",
        }
      );
      await refetch();
    } catch (e) {
      // Handle error
      console.error(e);
    }
  };

  const handleRecalculate = async () => {
    try {
      await recalculateInvoiceMutation.mutateAsync({
        invoiceId,
      });
      enqueueSnackbar(
        t(
          "invoiceDetails.invoiceRecalculateSuccess",
          "Charges recalculated successfully"
        ),
        {
          variant: "success",
        }
      );
      await refetch();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <InvoiceDetails
      invoice={data?.invoiceById}
      onSave={handleSave}
      onRecalculate={handleRecalculate}
      error={
        error || editInvoiceMutation.error || recalculateInvoiceMutation.error
      }
      saving={editInvoiceMutation.isLoading}
      recalculating={recalculateInvoiceMutation.isLoading}
      onSeparated={() => refetch()}
      {...restProps}
    />
  );
};

export default InvoiceDetailsContainer;
